import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { BREADCRUMB_SEPARATOR } from './types';

import styles from './OptoBreadCrumb.module.scss';

const URL_SUFFIX_TO_I18N_KEY: Record<string, string> = {
    // ── dashboard ────────────────────────────────────────────────────────────
    '/dashboard': 'breadcrumb.dashboard.root',
    '/dashboard/images': 'breadcrumb.dashboard.images',
    '/dashboard/graphs': 'breadcrumb.dashboard.graphs',
    '/dashboard/bioscope': 'breadcrumb.dashboard.bioscope',

    // ── weight ───────────────────────────────────────────────────────────────
    '/weight': 'breadcrumb.weight.root',
    '/weight/distribution': 'breadcrumb.weight.distribution',
    '/weight/growth': 'breadcrumb.weight.growth',
    '/weight/graphs': 'breadcrumb.weight.graphs',

    // ── lice ─────────────────────────────────────────────────────────────────
    '/lice': 'breadcrumb.lice.root',
    '/lice/detections': 'breadcrumb.lice.detections',
    '/lice/female': 'breadcrumb.lice.female',
    '/lice/ovigerousfemale': 'breadcrumb.lice.ovigerousFemale',
    '/lice/mobile': 'breadcrumb.lice.mobile',
    '/lice/caligus': 'breadcrumb.lice.caligus',
    '/lice/graphs': 'breadcrumb.lice.graphs',
    '/lice/lice-report': 'breadcrumb.lice.liceReport',

    // ── welfare ──────────────────────────────────────────────────────────────
    '/welfare': 'breadcrumb.welfare.root',
    '/welfare/scaleloss': 'breadcrumb.welfare.scaleloss',
    '/welfare/maturation': 'breadcrumb.welfare.maturation',
    '/welfare/maturation/detections': 'breadcrumb.welfare.maturationDetections',
    '/welfare/speed': 'breadcrumb.welfare.speed',
    '/welfare/wounds': 'breadcrumb.welfare.wounds',
    '/welfare/wounds/detections': 'breadcrumb.welfare.woundDetections',
    '/welfare/wounds/graphs': 'breadcrumb.welfare.woundGraphs',

    // ── behavior ─────────────────────────────────────────────────────────────
    '/behavior': 'breadcrumb.behavior.root',
    '/behavior/swim-speed': 'breadcrumb.behavior.swimSpeed',
    '/behavior/respiration': 'breadcrumb.behavior.respiration',
    '/behavior/tilt': 'breadcrumb.behavior.tilt',

    // ── health ───────────────────────────────────────────────────────────────
    '/health': 'healthModule.routes.health',
    '/health/body-wounds': 'healthModule.routes.bodyWounds',
    '/health/body-wounds/insight': 'healthModule.wounds.insight',
    '/health/body-wounds/laksvel': 'healthModule.wounds.laksvel',
    '/health/deformities': 'healthModule.routes.deformities',
    '/health/deformities/jaw': 'healthModule.routes.jaw',
    '/health/deformities/spine': 'healthModule.routes.spine',
    '/health/fin-status': 'healthModule.routes.finStatus',
    '/health/body-wounds/graphs': 'breadcrumb.health.bodyWoundGraphs',
    '/health/snout-wounds': 'breadcrumb.health.snoutWounds',
    '/health/maturation': 'breadcrumb.health.maturation',
    '/health/scale-loss': 'breadcrumb.health.scaleLoss',
    '/health/skin-bleeding': 'breadcrumb.health.skinBleeding',
    '/health/emaciation': 'breadcrumb.health.emaciation',
    '/health/deformities/gill-lid': 'breadcrumb.health.gillLid',
    '/health/cataract': 'breadcrumb.health.cataract',
    '/health/eye-damage': 'breadcrumb.health.eyeDamage',

    // ── reports ──────────────────────────────────────────────────────────────
    '/reports': 'reports.titles.mainPage',
    '/reports/harvest': 'breadcrumb.reports.harvestReport',
    '/reports/smolt': 'breadcrumb.reports.smoltReport',

    // ── misc cage pages ───────────────────────────────────────────────────────
    '/bioscope-image': 'breadcrumb.misc.bioscopeImage',
    '/lice-count': 'breadcrumb.misc.liceCount',
    '/winch': 'breadcrumb.misc.winch',
    '/settings': 'breadcrumb.misc.settings',
    '/move-bioscope': 'breadcrumb.misc.moveBioscope',
    '/change-pen': 'breadcrumb.misc.changePen',
    '/users': 'breadcrumb.misc.users',
    '/help': 'breadcrumb.misc.help',
    '/bioscope': 'breadcrumb.misc.bioscope',

    // ── location pages ────────────────────────────────────────────────────────
    '/feeding': 'breadcrumb.misc.feeding',
    '/operational-dashboard': 'breadcrumb.misc.operationalDashboard',
    '/video': 'breadcrumb.misc.video',
    '/compare': 'breadcrumb.misc.compare',
    '/lice-report': 'breadcrumb.misc.liceReport',
    '/fish-groups': 'breadcrumb.misc.fishGroups',
};

interface BreadcrumbPagePathProps {
    pagePath: string;
}

export default function BreadcrumbPagePath(props: BreadcrumbPagePathProps) {
    const { pagePath: urlpath } = props;
    const { pathname } = useLocation();
    const { t } = useTranslation();

    const basePath = pathname.slice(0, pathname.length - urlpath.length);
    const items = buildBreadcrumbItems(urlpath, basePath, t);

    if (items.length === 0) {
        return null;
    }

    return (
        <>
            {items.map((item, index) => (
                <div className={styles.lastPartContent} key={`last-part-${index}-${item.label}`}>
                    <span data-type="locationCageSeparator">{BREADCRUMB_SEPARATOR}</span>
                    {item.path != null ? (
                        <Link
                            className={`${styles.breadcrumbtokens} ${styles.breadcrumbtokensNavigable}`}
                            to={item.path}
                            title={item.label}>
                            {item.label}
                        </Link>
                    ) : (
                        <div className={styles.breadcrumbtokens} title={item.label}>
                            {item.label}
                        </div>
                    )}
                </div>
            ))}
        </>
    );
}

function segmentToDisplayToken(segment: string): string {
    const withSpaces = segment.replaceAll('-', ' ');
    if (withSpaces.length === 0) {
        return withSpaces;
    }
    return `${withSpaces[0].toUpperCase()}${withSpaces.slice(1)}`;
}

interface BreadcrumbItem {
    label: string;
    path: string | null;
}

function buildBreadcrumbItems(
    urlpath: string,
    basePath: string,
    translate: (key: string) => string
): BreadcrumbItem[] {
    const segments = urlpath.split('/').filter((x) => x);
    if (segments.length === 0) {
        return [];
    }

    return segments.map((_, i) => {
        const suffix = `/${segments.slice(0, i + 1).join('/')}`;
        const i18nKey = URL_SUFFIX_TO_I18N_KEY[suffix];
        // A segment with no entry is an id or a page added since; it shows as written.
        const label =
            i18nKey !== undefined ? translate(i18nKey) : segmentToDisplayToken(segments[i]);
        const isLast = i === segments.length - 1;
        return {
            label,
            path: isLast ? null : `${basePath}${suffix}`,
        };
    });
}
